// src/pages/receipts/details/index.jsx (ReceiptDetails)

import React, { useEffect, useRef, useState } from "react";
import "./index.css";
import OutletHead from "../../../../components/common/outlet-head";
import {
  Button,
  Modal,
  OverlayTrigger,
  Spinner,
  Tooltip
} from "react-bootstrap";
import {
  IoArrowBackOutline,
  IoClose,
  IoCloudDownload,
  IoDocumentTextOutline,
  IoEye,
  IoSend,
  IoSettingsOutline,
  IoShareSocial,
  IoTrashOutline
} from "react-icons/io5";
import { BsPrinter } from "react-icons/bs";
import { useLocation, useNavigate } from "react-router-dom";
import generatePDF from "react-to-pdf";
import ReceiptTemplate from "../../../../components/receipts/format";
import { render } from "@react-email/components";
import { useAuth } from "../../../../context/AuthContext";
import { useReactToPrint } from "react-to-print";
import EmailInvoiceTemplate from "../../../../components/invoices/email-invoice";
import { db } from "../../../../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot
} from "firebase/firestore";
import { toast } from "react-toastify";

function ReceiptDetails() {
  const targetInvoiceRef = useRef();

  const { user, businessProfile } = useAuth();
  const { bid } = user.claims;
  const location = useLocation();
  const { state } = location;
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [data, setData] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [showDelete, setShowDelete] = useState(false);

  const handleClose = () => setShowDelete(false);
  const handleShow = () => setShowDelete(true);

  function getBookingData(id) {
    const unsubscribe = onSnapshot(
      doc(db, "Businesses", bid, "Bookings", id),
      (docSnap) => {
        setBookingData({ id: docSnap.id, ...docSnap.data() });
        setTimeout(() => {
          setIsLoading(false);
        }, 250);
      }
    );

    return () => unsubscribe();
  }

  async function getInvoiceData(bookingID, invoiceIDs) {
    const tempDocs = [];
    for (const invoice of invoiceIDs) {
      const docRef = doc(
        db,
        "Businesses",
        bid,
        "Bookings",
        bookingID,
        "Invoices",
        invoice.id
      );
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        tempDocs.push({ id: docSnap.id, ...docSnap.data() });
      } else {
        console.log(`Invoice with ID ${invoice.id} not found`);
      }
    }

    setInvoiceData(tempDocs);
  }

  function getReceiptData(id) {
    const unsubscribe = onSnapshot(
      doc(db, "Businesses", bid, "Receipts", id),
      (docSnap) => {
        const receipt = docSnap.data();
        if (!receipt) {
          setData(null);
          setInvoiceData(null);
          setBookingData(null);
          return;
        }
        getInvoiceData(receipt.bookingID, receipt.invoiceIDs || []);
        getBookingData(receipt.bookingID);
        setData({ id: docSnap.id, ...receipt });
      }
    );

    return () => unsubscribe();
  }

  useEffect(() => {
    if (state) {
      const id = state.id;
      getReceiptData(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function sendMail(invoiceNumber, recipientAddress) {
    try {
      const recipientArray = [];
      recipientArray.push(recipientAddress);
      const emailHtml = render(
        <body>
          <EmailInvoiceTemplate
            invoiceData={data}
            bookingData={bookingData}
            businessProfile={businessProfile}
          />
        </body>
      );

      const emailConfig = {
        to: recipientArray,
        message: {
          subject: `Invoice / Receipt No. ${invoiceNumber}`,
          text: "Thank you for your prompt payment.",
          html: emailHtml
        }
      };

      const docRef = await addDoc(collection(db, "mail"), emailConfig);

      if (docRef.id) {
        setIsLoading(false);
        toast.success("Invoice Sent via Email");
      }
    } catch (error) {
      console.log("Sending Invoice Email [Error] : ", error);
      setIsLoading(false);
    }
  }

  const handlePrint = useReactToPrint({
    documentTitle: data && data.id,
    content: () => targetInvoiceRef.current,
    onBeforePrint: () => setIsProcessing(true),
    onAfterPrint: () => setIsProcessing(false)
  });

  async function handleDeleteReceipt() {
    if (!data || !data.id) return;

    try {
      setIsDeleting(true);
      const id = data.id;

      await deleteDoc(
        doc(db, "Businesses", bid, "Receipts", id)
      );

      toast.success("Receipt deleted successfully.");
      setIsDeleting(false);
      handleClose();
      navigate(-1);
    } catch (error) {
      console.error("Delete receipt [Error]: ", error);
      toast.error("Failed to delete receipt.");
      setIsDeleting(false);
    }
  }

  return (
    <section className="section">
      <OutletHead
        left={
          <div className="d-flex align-items-center">
            <Button
              variant="light"
              size="sm"
              className="p-2 me-3 rounded-circle"
              onClick={() => navigate(-1)}
            >
              <IoArrowBackOutline size={24} />
            </Button>
            <h5 className="mb-0 fw-600">{data && data.id}</h5>
          </div>
        }
        right={
          <div className="web-view d-flex align-items-center justify-content-end">
            <Button
              variant="light"
              type="submit"
              className="border py-2 px-4 me-3"
            >
              <IoSettingsOutline size={20} />
              <span className="ps-2">Settings</span>
            </Button>
            <Button
              variant="outline-primary"
              type="submit"
              className="py-2 px-4"
              size="sm"
            >
              <IoDocumentTextOutline size={18} />
              <span className="ps-2">Export</span>
            </Button>
          </div>
        }
      />
      <div className="inner-wrapper border-top">
        <div className="row">
          <div className="col-md-12 bg-light">
            <div className="d-flex align-items-center justify-content-center">
              <div className="invoice-container">
                <div className="row sticky-top py-3 bg-light">
                  <div className="col-8 d-flex justify-content-start">
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        <Tooltip>
                          <small>View</small>
                        </Tooltip>
                      }
                    >
                      <Button
                        variant="white icon-button p-0 me-2"
                        onClick={() => null}
                      >
                        <IoEye size={18} />
                      </Button>
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        <Tooltip>
                          <small>Download</small>
                        </Tooltip>
                      }
                    >
                      <Button
                        variant="white icon-button p-0 me-2"
                        onClick={() =>
                          generatePDF(targetInvoiceRef, {
                            filename: `${data?.id || "receipt"}.pdf`
                          })
                        }
                      >
                        <IoCloudDownload size={18} />
                      </Button>
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        <Tooltip>
                          <small>Send</small>
                        </Tooltip>
                      }
                    >
                      <Button
                        variant="white icon-button p-0 me-2"
                        onClick={() => {
                          if (
                            bookingData &&
                            bookingData.partyDetails &&
                            bookingData.partyDetails.email
                          ) {
                            sendMail(
                              data?.id,
                              bookingData.partyDetails.email
                            );
                          } else {
                            toast.error("Party's email doesn't exist!");
                          }
                        }}
                      >
                        <IoSend size={18} />
                      </Button>
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        <Tooltip>
                          <small>Share</small>
                        </Tooltip>
                      }
                    >
                      <Button
                        variant="white icon-button p-0 me-2"
                        onClick={() => null}
                      >
                        <IoShareSocial size={18} />
                      </Button>
                    </OverlayTrigger>
                  </div>
                  <div className="col-4 d-flex justify-content-end">
                    <Button
                      variant="primary"
                      size="sm"
                      className="px-3"
                      onClick={() => {
                        setIsProcessing(true);
                        handlePrint();
                      }}
                    >
                      {isProcessing ? (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                        />
                      ) : (
                        <>
                          <BsPrinter size={18} />
                          <span className="ms-2">Print</span>
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline-danger"
                      type="button"
                      className="py-2 px-3 ms-3"
                      onClick={handleShow}
                    >
                      <IoTrashOutline size={18} />
                    </Button>
                  </div>
                </div>
                <div className="mt-5 bg-white">
                  <ReceiptTemplate
                    isLoading={isLoading}
                    targetInvoiceRef={targetInvoiceRef}
                    navigate={navigate}
                    state={bookingData}
                    invoiceData={invoiceData}
                    receiptData={data}
                    businessProfile={businessProfile}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={showDelete}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
        contentClassName="border-0 shadow-sm"
        centered
      >
        <div className="bg-light overflow-hidden rounded">
          <div className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0 p-3">Delete</h5>
            <Button
              variant="white"
              className="rounded border-0 px-3"
              onClick={() => handleClose()}
            >
              <IoClose size={20} />
            </Button>
          </div>
        </div>
        <div className="border-top px-4 pt-4">
          <h6 className="mb-0">
            Are you sure, you want to delete this receipt?
          </h6>
          <div className="pb-3 pt-2">
            <p className="mb-0 text-muted small">
              <span className="fw-600 text-decoration-underline">
                Warning
              </span>{" "}
              - Deleting this receipt will permanently remove it and
              delete the associated ledger entry.
            </p>
          </div>
        </div>
        <div className="d-flex align-items-center justify-content-end px-3 pb-3 mt-2">
          <Button
            disabled={isDeleting && isDeleting}
            variant="light"
            onClick={handleClose}
            className="border px-5 py-2 me-3"
          >
            Cancel
          </Button>
          <Button
            disabled={isDeleting && isDeleting}
            variant="danger"
            className="px-5 py-2 btn-sm-width"
            onClick={handleDeleteReceipt}
          >
            {isDeleting && isDeleting ? (
              <div>
                <Spinner size="sm" />
              </div>
            ) : (
              <span>Delete</span>
            )}
          </Button>
        </div>
      </Modal>
    </section>
  );
}

export default ReceiptDetails;